// @flow

import * as React from 'react';
import { translate } from 'react-i18next';
import styled, { withTheme } from 'styled-components';
import { Card, CardHeader, CardTitle } from 'statsradio/themes/v2/Stylesheet';
import { MenuItem, Select } from '@material-ui/core';
import type { PropositionSummaryTimeRangeValues } from 'statsradio/modules/salesDashboard/domain/PropositionSummaryQuery';
import { PropositionSummaryQuery, PropositionSummaryTimeRanges } from 'statsradio/modules/salesDashboard/domain/PropositionSummaryQuery';
import PropositionSummary from 'statsradio/modules/salesDashboard/domain/PropositionSummary';
import { SalesMember } from 'statsradio/modules/salesMembers/domain/SalesMember';
import { connect } from 'react-redux';
import DashboardCampaignApi from 'statsradio/modules/campaigns/services/DashboardCampaignApi';
import PropositionSummaryMetric from 'statsradio/modules/salesDashboard/components/PropositionSummary/PropositionSummaryMetric';
import Radio from 'common/domain/radio';
import AntennaPerformance from 'statsradio/modules/salesDashboard/components/AntennaPerformance';
import { getToken } from 'statsradio/modules/authentication/session/selectors/sessionSelectors';
import withSplashLoader from 'common/components/withSplashLoader';

const StyledCard = styled(Card)`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 
    ${({ theme }) => theme.V2.metrics.margin.normal}px 
    ${({ theme }) => theme.V2.metrics.margin.normal}px;
`;

const StyledCardHeader = styled(CardHeader)`
  margin: 0;
  margin-bottom: ${({ theme }) => theme.V2.metrics.margin.small}px;
  display:flex;
`;

const StyledMenuItem = styled(MenuItem)`
  font-size: ${({ theme }) => theme.V2.fonts.button.size}px;
  color: ${({ theme }) => theme.V2.colors.text.cardHeader};
`;

const StyledTitle = styled(CardTitle)`
  display: flex;
  flex: 1;
  align-items: center;
`;

const ActionText = styled.div`
  display: flex;
  align-items: center;
  &:hover {
    cursor: pointer;
  }
`;

const StyledCardContent = withSplashLoader('isFetching', 'small')(styled.div`
  display:flex;
  flex-direction: row;
  justify-content: space-between;
  margin-bottom: ${({ theme }) => theme.V2.metrics.margin.normal}px
`);

const ErrorLabel = styled.div`
  color: ${({ theme }) => theme.V2.colors.error}
`;


type Props = {
  t: any,
  className?: any,
  token: string,
  radio: Radio,
  selectedSalesMember?: SalesMember
}

type State = {
  selectedTimeRange: PropositionSummaryTimeRangeValues,
  isFetching: boolean,
  error: boolean,
  summary?: PropositionSummary,
}

type SelectTimeRangeEvent = { target: { value: PropositionSummaryTimeRangeValues } };

export class PropositionSummaryCard extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      selectedTimeRange: PropositionSummaryTimeRanges.LAST_30_DAYS,
      isFetching: false,
      summary: undefined,
      error: false,
    };
  }

  componentDidMount = () => {
    this.fetchSummary(new PropositionSummaryQuery({ timeRange: this.state.selectedTimeRange, selectedSalesMember: this.props.selectedSalesMember }));
  };

  componentDidUpdate(prevProps: Props) {
    const salesMemberChanged = prevProps.selectedSalesMember !== this.props.selectedSalesMember;
    const radioChanged = prevProps.radio !== this.props.radio;

    if (salesMemberChanged || radioChanged) {
      this.fetchSummary(new PropositionSummaryQuery({ timeRange: this.state.selectedTimeRange, selectedSalesMember: this.props.selectedSalesMember }));
    }
  }

  fetchSummary = async (query: PropositionSummaryQuery) => {
    const { token, radio } = this.props;
    try {
      this.setState({ isFetching: true, error: false });
      const data = await DashboardCampaignApi.fetchPropositionDataSummary(token, radio.callSign, query);
      this.setState({ summary: data, isFetching: false });
    } catch (e) {
      this.setState({ isFetching: false, error: true });
    }
  };

  onTimeRangeChange = ({ target }: SelectTimeRangeEvent) => {
    this.setState({ selectedTimeRange: target.value });
    this.fetchSummary(new PropositionSummaryQuery({ timeRange: target.value, selectedSalesMember: this.props.selectedSalesMember }));
  };

  getTimeRangeChoice = (value: any) => {
    const { t } = this.props;
    return (
      <StyledMenuItem
        key={value}
        name={value}
        value={value}
      >
        {t(`timeRangeChoices.${value}`)}
      </StyledMenuItem>
    );
  };

  render() {
    const { t, className, token, radio }: Props = this.props;
    const { selectedTimeRange, summary, isFetching, error }: State = this.state;

    return (
      <StyledCard className={className}>
        <StyledCardHeader>
          <StyledTitle>
            {t('commonSales:propositions')}
          </StyledTitle>
          <ActionText>
            <Select
              disableUnderline
              value={selectedTimeRange}
              onChange={this.onTimeRangeChange}
              variant={'standard'}
            >
              {
                Object.values(PropositionSummaryTimeRanges).map(this.getTimeRangeChoice)
              }
            </Select>
          </ActionText>
        </StyledCardHeader>

        {error &&
        <ErrorLabel>
          <span>{t('couldNotFetchSummary')} </span>
        </ErrorLabel>
        }

        {summary && !error &&
        <StyledCardContent isFetching={isFetching}>
          <PropositionSummaryMetric
            label={t('createdPropositions')}
            value={summary.created}
            totalCost={summary.createdTotalCost}
          />

          <PropositionSummaryMetric
            label={t('sentPropositions')}
            value={summary.sent}
            totalCost={summary.sentTotalCost}
          />

          <PropositionSummaryMetric
            label={t('acceptedPropositions')}
            value={summary.accepted}
            totalCost={summary.acceptedTotalCost}
          />
        </StyledCardContent>
        }
        <AntennaPerformance radio={radio} token={token} />
      </StyledCard>
    );
  }
}

function mapStateToProps(state) {
  return {
    token: getToken(state),
    radio: state.session.radio,
  };
}

export default withTheme(translate('salesDashboard', 'commonSales')(connect(mapStateToProps)(PropositionSummaryCard)));



// WEBPACK FOOTER //
// ./src/statsradio/modules/salesDashboard/components/PropositionSummary/PropositionSummaryCard.js